"use client";

import { useEffect, useRef } from "react";

import { env } from "@/lib/env/client";

import {
  CONSENT_DENIED,
  CONSENT_GRANTED,
  type GtmConsentState,
  type TcfData,
} from "./types";

const consentMode = env.NEXT_PUBLIC_CONSENT_MODE;
const cookiebotCbid = env.NEXT_PUBLIC_COOKIEBOT_CBID;
const cookiebotShadowCbid = env.NEXT_PUBLIC_COOKIEBOT_SHADOW_CBID;
const cookiebotShadowRegions = env.NEXT_PUBLIC_COOKIEBOT_SHADOW_REGIONS;

const COOKIEBOT_FALLBACK_DELAY_MS = 5000;

// --- GTM Consent Mode v2 ---

function setGtmConsentDefault(state: GtmConsentState): void {
  window.gtag?.("consent", "default", { ...state, wait_for_update: 500 });
}

function updateGtmConsent(state: GtmConsentState): void {
  window.gtag?.("consent", "update", state);
}

function mapTcfToGtmConsent(
  purposeConsents: Record<number, boolean>,
): GtmConsentState {
  const allowed = (id: number) => purposeConsents[id] === true;
  return {
    ad_storage: allowed(1) ? "granted" : "denied",
    ad_user_data: allowed(1) ? "granted" : "denied",
    ad_personalization: allowed(3) && allowed(4) ? "granted" : "denied",
    analytics_storage: allowed(1) ? "granted" : "denied",
  };
}

// --- TCF API ---

function onTcfConsentChange(callback: (data: TcfData) => void): void {
  window.__tcfapi?.(
    "addEventListener",
    2,
    (data: TcfData, success: boolean) => {
      if (!success) return;
      if (
        data.eventStatus === "useractioncomplete" ||
        data.eventStatus === "tcloaded"
      ) {
        callback(data);
      }
    },
  );
}

function pingTcfApi(): Promise<TcfData | null> {
  return new Promise((resolve) => {
    if (!window.__tcfapi) return resolve(null);
    window.__tcfapi("ping", 2, (data, success) =>
      resolve(success ? data : null),
    );
  });
}

// --- Cookiebot ---

function buildGeoregions(regions: string, shadowCbid: string): string {
  return `{'region':'${regions}','cbid':'${shadowCbid}'}`;
}

function loadCookiebot(
  cbid: string,
  opts?: { blockingMode?: boolean; georegions?: string },
): void {
  if (document.getElementById("cookiebot-script")) return;
  const script = document.createElement("script");
  script.id = "cookiebot-script";
  script.src = "https://consent.cookiebot.com/uc.js";
  script.dataset.cbid = cbid;
  if (opts?.georegions) {
    script.dataset.georegions = opts.georegions;
  }
  if (opts?.blockingMode !== false) {
    script.dataset.blockingmode = "auto";
  }
  script.type = "text/javascript";
  document.head.appendChild(script);
}

function startCookiebotFallback(cbid: string, georegions?: string): void {
  setTimeout(async () => {
    const ping = await pingTcfApi();
    if (ping?.cmpStatus !== "loaded") {
      loadCookiebot(cbid, { georegions });
    }
  }, COOKIEBOT_FALLBACK_DELAY_MS);
}

// --- Component ---

/**
 * Client component — listens for TCF consent events and updates GTM Consent
 * Mode v2 accordingly. Optionally loads Cookiebot as a fallback CMP.
 *
 * NOTE: consent defaults (deny-all) are already set in the server-rendered
 * GTM init script via ConsentScripts. This component sets them again as a
 * safety net in case ConsentScripts ran without GTM enabled, or the page
 * hydrates before the beforeInteractive script executes.
 *
 * No DOM output — renders null.
 */
export function ConsentListener() {
  const initialized = useRef<boolean>(false);

  useEffect(() => {
    if (initialized.current || !consentMode || consentMode === "none") {
      if (consentMode === "none") {
        // AU: no consent UI — grant all immediately so GTM tags can fire
        updateGtmConsent(CONSENT_GRANTED);
      }
      return;
    }
    initialized.current = true;

    // Only set deny-all defaults for GDPR regions; non-GDPR regions already
    // start with granted defaults from the server-rendered GTM init script.
    if (
      consentMode === "own-cookiebot" ||
      consentMode === "publisher-cmp-tag"
    ) {
      setGtmConsentDefault(CONSENT_DENIED);
    }

    if (consentMode === "own-cookiebot" && cookiebotCbid) {
      const georegions =
        cookiebotShadowCbid && cookiebotShadowRegions
          ? buildGeoregions(cookiebotShadowRegions, cookiebotShadowCbid)
          : undefined;
      loadCookiebot(cookiebotCbid, { georegions });
    }

    if (consentMode === "none-with-footer-link") {
      // US/CA: grant consent immediately (no banner), and if a CBID is
      // configured, load Cookiebot silently so Cookiebot.renew() works from
      // the footer bar. CA may not have a CBID yet — consent is still granted.
      if (cookiebotCbid) {
        const georegions =
          cookiebotShadowCbid && cookiebotShadowRegions
            ? buildGeoregions(cookiebotShadowRegions, cookiebotShadowCbid)
            : undefined;
        loadCookiebot(cookiebotCbid, {
          blockingMode: false,
          georegions,
        });
      }
      updateGtmConsent(CONSENT_GRANTED);
      return;
    }

    onTcfConsentChange((data) => {
      if (data.gdprApplies === false) {
        updateGtmConsent(CONSENT_GRANTED);
        return;
      }
      updateGtmConsent(mapTcfToGtmConsent(data.purpose?.consents ?? {}));
    });

    if (consentMode === "publisher-cmp-tag" && cookiebotCbid) {
      const georegions =
        cookiebotShadowCbid && cookiebotShadowRegions
          ? buildGeoregions(cookiebotShadowRegions, cookiebotShadowCbid)
          : undefined;
      startCookiebotFallback(cookiebotCbid, georegions);
    }
  }, []);

  return null;
}
